<template>
  <div class="allocation">
    <h1 class="general-title">
      Volumes Monitoring
    </h1>
    <div class="allocation_card">
      <div class="header_content">
        <div class="">
          <h3 v-if="graphType === 'allocation'" class="titles">
            Allocation
          </h3>
          <h3 v-else-if="graphType === 'online'" class="titles l-flex-row l-align-center">
            Collect Online
            <el-tooltip
              placement="top"
              content="Number of web hits collected"
            >
              <div class="info-icon">
                <svg-icon
                  name="info"
                />
              </div>
            </el-tooltip>
          </h3>
          <h3 v-else class="titles">
            Collect Offline
          </h3>
        </div>
        <div class="l-flex-row select-group">
          <div class="l-flex-row select-own">
            <span class="select-title">Time Window:</span>
            <el-date-picker
              v-model="recencyValue"
              name="end_date"
              :editable="true"
              :clearable="false"
              type="daterange"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :picker-options="endDateOptions"
              v-validate="'date_format:YYYY-MM-DD|required'"
              placeholder="Allocation range date"
              size="small"
              class="dt-range"
              :disabled="isLoading"
              @change="retrieveAllocation()"
            />
          </div>

          <div class="l-flex-row select-own">
            <span class="select-title">Graph Type:</span>
            <el-select v-model="graphType" :disabled="isLoading" class="select-dropdown">
              <el-option
                v-for="type in graphTypeOptions"
                :key="type.value"
                :label="type.label"
                :value="type.value"
              />
            </el-select>
          </div>
        </div>
      </div>
      <div class="data-content">
        <spinner
          v-if="isLoading"
          width="100"
          height="100"
          class="l-flex-row--center l-align-center"
        />
        <template v-else>
          <div
            class="content"
            v-if="graphType === 'allocation'"
          >
            <div class="content-tab">
              <ul>
                <li
                  @click="activeSubTab = 'account'"
                  :class="{'active' : activeSubTab === 'account'}"
                >
                  Account
                </li>
                <li
                  @click="activeSubTab = 'audience'"
                  :class="{'active' : activeSubTab === 'audience'}"
                >
                  Audience allocation by source
                </li>
              </ul>
              <div
                class="totals"
                v-if="allocationData && allocationData.length > 0 && activeSubTab === 'account'"
              >
                <div
                  class="total alloc"
                  v-if="totalAllocations !== '0'"
                  @click="toggleLegend('allocationSelected')"
                  :class="{ 'unactive': !allocationSelected }"
                >
                  Total Allocation <strong>{{ totalAllocations }}</strong>
                </div>
                <div
                  class="total affect"
                  v-if="isKibanaAllowed"
                  :class="{ 'unactive': !affectationSelected }"
                  @click="toggleLegend('affectationSelected')"
                >
                  Total Affectation <strong>{{ totalAllocationsKibana }}</strong>
                </div>
              </div>
            </div>
            <div
              class="account-content"
              v-if="activeSubTab === 'account'"
            >
              <div
                class="l-flex-column no-alloc-data"
                v-if="allocationData && allocationData.length === 0"
              >
                <p class="zero-state-message">
                  No allocation data available on this account.
                </p>
              </div>
              <AllocationGraph
                v-else
                class-name="w-linechart--attribution"
                :allocation-metrics="allocationMetrics"
                :has-account-access-to-kibana-stats="isKibanaAllowed"
                :affectation-selected="affectationSelected"
                :allocation-selected="allocationSelected"
              />
            </div>
            <div
              class="audience-content"
              v-if="activeSubTab === 'audience'"
            >
              <div
                class="l-flex-column no-alloc-data"
                v-if="allocationData && allocationData.length === 0"
              >
                <p class="zero-state-message">
                  No allocation data available on this account.
                </p>
              </div>
              <AllocationDoughnuts
                v-else
                class-name="w-doughnutchart--attribution"
                :allocation-metrics="allocationMetrics"
              />
            </div>
          </div>

          <div
            class="content"
            v-if="graphType === 'online'"
          >
            <div class="content-tab">
              <AllocationCollectOnline
                v-if="collectOnline"
                :collect-online-metrics="collectOnline"
              />
              <div
                class="l-flex-column no-alloc-data full-size"
                v-else
              >
                <p class="zero-state-message">
                  No collect online data available on this account.
                </p>
              </div>
            </div>
          </div>
          <div
            class="content"
            v-if="graphType === 'offline'"
          >
            <div class="content-tab">
              <div
                class="full-size"
                v-if="collectOffline && !collectOffline.message"
              >
                <div
                  class="totals l-flex-row--rev"
                  v-if="graphType === 'collect'"
                >
                  <div
                    class="total success"
                    :class="{ 'unactive': !successOfflineSelected }"
                    @click="toggleLegend('successOfflineSelected')"
                    v-if="totalCollectOffline('success') && totalCollectOffline('success') > 0"
                  >
                    Success <strong>{{ totalCollectOffline('successPercent') }} %</strong>
                  </div>
                  <div
                    class="total failed"
                    v-if="totalCollectOffline('failed') && totalCollectOffline('failed') > 0"
                    @click="toggleLegend('failOfflineSelected')"
                    :class="{ 'unactive': !failOfflineSelected }"
                  >
                    Fail <strong>{{ totalCollectOffline('failedPercent') }} %</strong>
                  </div>
                </div>
              </div>
              <AllocationCollectOffline
                v-if="collectOffline && !collectOffline.message"
                :data="collectOffline"
                :success-offline-selected="successOfflineSelected"
                :fail-offline-selected="failOfflineSelected"
              />
              <div
                class="l-flex-column no-alloc-data full-size"
                v-else
              >
                <p class="zero-state-message">
                  No collect offline data available on this account.
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import dayjs from 'dayjs'
import { getAccountAllocationMetrics } from '@/store/dashboardAccount/helpers'
import { formatNumber } from '@/utils/helpers.js'
import AllocationGraph from './AllocationGraph.vue'
import AllocationDoughnuts from './AllocationDoughnuts.vue'
import AllocationCollectOffline from './AllocationCollectOffline.vue'
import AllocationCollectOnline from './AllocationCollectOnline.vue'

export default {
    components: { AllocationGraph, AllocationDoughnuts, AllocationCollectOffline, AllocationCollectOnline },
    data () {
        const defaultEndDate = dayjs()
        const defaultStartDate = dayjs().subtract(7, 'days')
        const dateRange = [defaultStartDate.toISOString(), defaultEndDate.toISOString()]
        return {
            graphType: 'online',
            graphTypeOptions: [
                { value: 'allocation', label: 'Allocation' },
                { value: 'online', label: 'Collect Online' },
                { value: 'offline', label: 'Collect Offline' },
            ],
            periodSelected: 7,
            periodOptions: [
                { value: 7, label: 'Last Week' },
                { value: 14, label: 'Last 2 Weeks' },
                { value: 21, label: 'Last 3 Weeks' },
                { value: 30, label: 'Last Month' },
            ],


            activeSubTab: 'account',
            isLoading: false,
            recencyValue: dateRange,
            affectationSelected: true,
            allocationSelected: true,
            successOfflineSelected: true,
            failOfflineSelected: true
        }
    },

    computed: {
        ...mapState('dashboardAccount', {
            currentAllocationTimeUnit: state => state.currentAllocationTimeUnit,
            allocationTopLoading: state => state.allocationTop.loading,
            // Account
            allocationData: state => state.allocationData,
            kibanaData: state => state.kibanaData,
            dateRange: state => state.dateRange
        }),

        ...mapState('account', {
            accountId: state => state.accountId
        }),

        ...mapGetters('dashboardAccount', ['isKibanaAllowed', 'collectOffline', 'collectOnline']),

        allocationMetrics() {
            return getAccountAllocationMetrics(this.allocationData, this.currentAllocationTimeUnit, this.kibanaData, this.dateRange[0], this.dateRange[1])
        },

        totalAllocations() {
            return formatNumber(this.allocationMetrics.totalAllocations)
        },

        totalAllocationsKibana() {
            return formatNumber(this.allocationMetrics.totalAllocationsKibana)
        },
        endDateOptions() {
            const maxStartDate = new Date().setDate(new Date().getDate() - 31)
            return {
                disabledDate(time) {
                    const now = new Date()
                    return !(time.getTime() < now && time.getTime() >= maxStartDate)
                }
            }
        },

    },

    mounted() {
        this.retrieveAllocation()
    },

    methods: {
        ...mapMutations('dashboardAccount', [
            'setAllocationData',
            'setKibanaData',
            'setDateRange'
        ]),

        ...mapActions('dashboardAccount', ['getAllAllocations', 'getKibanaData', 'getCollectOffline', 'getCollectOnline']),
        retrieveRecenciesValue(value) {
            const start = dayjs()
                .subtract(value, 'days')
                .format('YYYY-MM-DDTHH:mm:ss[Z]')
            const end = dayjs().format('YYYY-MM-DDTHH:mm:ss[Z]')
            return [start, end]
        },
        async retrieveAllocation() {
            this.isLoading = true
            this.retrieveRecenciesValue(this.recencyValue)
            this.setDateRange(this.recencyValue)
            await this.getAllAllocations({
                start: this.recencyValue[0],
                end: this.recencyValue[1]
            })
            if (this.isKibanaAllowed) {
                await this.getKibanaData({
                    start: this.recencyValue[0],
                    end: this.recencyValue[1]
                })
            }
            await this.getCollectOffline({
                start_date: this.recencyValue[0],
                end_date: this.recencyValue[1]
            })
            await this.getCollectOnline({
                start_date: this.recencyValue[0],
                end_date: this.recencyValue[1]
            })

            this.isLoading = false
        },
        totalCollectOffline(value) {
            return this.collectOffline?.total[value] || 0
        },
        toggleLegend(legendName) {
            this[legendName] = !this[legendName]
        }
    }
}
</script>
<style lang="scss" scoped>
.allocation {
  .allocation_card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px 18px;
    margin-top: 20px;
    .header_content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      .titles {
        font-size: 16px;
        font-weight: bold;
        gap: 6px;
        .info-icon {
          cursor: pointer;
          border-radius: 100%;
          border: 1px solid #c5c5c5;
          padding: 3px 6px;
          font-size: 10px;
      }
    }
  }
  .general-title {
    font-size: 15px;
    margin-bottom: 25px;
  }

  .select-group {
    gap: 20px;
    .select-own {
      align-items: center;
      .select-title {
        font-size: 15px;
        color: #475765;
        margin-right: 10px;
      }
      .select-dropdown {
        width: 200px;
      }
    }
  }

  .data-content {
    height: 350px;
    .content {
      width: 100%;
      margin-top: 5px;
      .content-tab {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0;
        flex-wrap: wrap;
        min-height: 47px;
        ul {
          display: flex;
          li {
            display: flex;
            color: #485465;
            cursor: pointer;
            padding: 13px 0;
            opacity: 0.4;
            font-weight: bold;
            font-size: 12px;
            &:last-child {
              margin-left: 48px;
            }
            &::before {
              margin-top: 4px;
              content: "";
              background: var(--color-accent);
              display: inline-block;
              width: 9px;
              height: 9px;
              border-radius: 100%;
              margin-right: 6px;

            }
            &.active {
              opacity: 1;
            }
          }
        }
      }
      .no-alloc-data {
        height: 356px;
        justify-content: center;
      }
    }
  }

  .totals {
    display: flex;
    gap: 20px;
    color: #809FB8;
    font-size: 14px;
    .total {
      display: flex;
      align-items: center;
      gap: 12px;
      background:  rgba(217, 225, 231, 0.2);
      border-radius: 13px;
      padding: 13px;
      cursor: pointer;
      &.unactive {
        background: #e3e3e3;
        opacity: 0.4;
      }
      &.alloc::before {
        background: #796BF4;
      }
      &.affect::before {
        background: var(--color-accent);
      }
      &.success::before {
        background: #77b22a;
      }
      &.failed::before {
        background: #d73c41;
      }
      &::before {
        content: '';
        background: black;
        width: 14px;
        height: 14px;
        border-radius: 3px;
      }
    }
  }

  .el-range-input {
    color: #A3ABB2;
  }

  .audience-content {
    height: 333px;
    width: 100%;
  }
}
}
</style>
